import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors, Print, checkPermission, SocketEvents } from 'utils';
import {
  Table,
  Pagination,
  Tooltip,
  Input,
  DatePicker,
  Select,
  Modal,
  PaperClip,
  ModalPayment,
  User,
  Render,
  FileUploadPlaceholder,
} from 'components';
import { ChatService, GeneralService, NotificationService } from 'services';
import {
  SendIcon,
  AddIcon,
  PaperclipIcon,
  Document2Icon,
  WarningIcon,
  BackIcon,
  ClockIcon,
  ImageIcon,
} from 'assets/icons';
import moment from 'moment';
import { Alert, UserImage, WhiteImage, Logo } from 'assets/img';
import * as Jetemit from 'jetemit';
import ModalCreate from './modal-create';
import ModalFiles from './modal-view-files';
import qs from 'query-string';
import AddMembership from '../memberships/add-membership';
import { Chat } from 'types';
import { StorageService } from 'services';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

const INITIAL_STATE = {};

class ChatScreen extends React.Component<Props> {
  messagesEnd: any = null;
  notificationsEnd: any = null;
  textareaRef: any = null;
  timer: any = undefined;

  state = {
    messages: [],
    form: {
      search: '',
      message: '',
    },
    textareaHeight: 55.8,
    unsubscriber: () => {},
    page: 1,
    last_page: 1,
    // NOTA: el tipo Chat.id es `number` (no admite null), pero en runtime este
    // campo puede valer `null` temporalmente cuando el chat es "pendiente"
    // (creado desde modal-lawyers.tsx, aún sin guardar en BD). Se usa `as any`
    // al setearlo para no romper la compilación; todo el código que lee
    // chat.id ya contempla este caso (ver send() y las condiciones de Render
    // en el header/textarea).
    chat: null as Chat | null,
    visible: false,
    visible_consumed: false,
    visible_payment: false,
    visible_files: false,
    sending_file: false,
    sending_file_placeholder: false,
    uploading_is_image: false,
    data: [],
    plan_user: null,
    membership_status: Constants.ACCES_STATUS.ACCESS_DENIED,
    price: 0,
    currentUser: null,
    item: null,
    visible_membership: false,
    // Bandeja de comunicados ("Legalmente"), tratada como un chat más de
    // solo lectura -- ver openNotifications()/loadNotifications() más abajo.
    notifications: [] as any[],
    notifications_page: 1,
    notifications_pages: 1,
    notifications_loading: false,
    notifications_loaded: false,
    unread_notifications_count: 0,
    viewing_notifications: false,
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Consulta un abogado',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    const params = qs.parse(this.props.location.search);

    if (params.chat_id) {
      this.loadMessages(Number(params.chat_id));
    }

    /**
     * POR QUÉ: cuando se viene desde "Ubica tu abogado" (modal-lawyers.tsx)
     * ya no se crea el chat de inmediato; en su lugar se navega aquí y el
     * abogado elegido se guarda en Redux (action SET_PENDING_LAWYER) en vez
     * de pasarse por location.state, porque este proyecto usa HashRouter y
     * HashRouter no soporta location.state (confirmado en consola: "Hash
     * history cannot push state; it is ignored").
     *
     * PARA QUÉ: con esto armamos un "chat virtual" en memoria (sin id, sin
     * llamar a ChatService.view) para que la UI muestre la conversación vacía
     * con el abogado seleccionado y el usuario pueda escribir su primer
     * mensaje. El registro real en BD (ChatService.save) ocurre recién en
     * `send()` cuando se envía ese primer mensaje. Inmediatamente después de
     * leerlo, se limpia del store (SET_PENDING_LAWYER -> null) para que no
     * quede "pegado" si el usuario vuelve a entrar a /client/chat sin pasar
     * por el directorio de abogados.
     *
     * QUÉ PUEDE AFECTAR: mientras `chat.id` sea null, hay que evitar llamar a
     * endpoints que requieren chat_id (ChatService.view, ChatService.send,
     * adjuntar archivos, marcar mensajes como vistos). Ver los ajustes en
     * `send()` y en el render del header/textarea más abajo.
     */
    const pendingLawyer = this.props.pending_lawyer;
    if (pendingLawyer && !params.chat_id) {
      this.setState({
        chat: {
          id: null,
          type: Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES,
          status: Constants.CHAT_STATUS.ACTIVE,
        } as any,
        currentUser: pendingLawyer,
        messages: [],
      });

      // Limpia el valor del store apenas se consume, para que no se reutilice
      // por error en una visita posterior a esta pantalla.
      this.props.dispatch({
        type: 'SET_PENDING_LAWYER',
        payload: null,
      });
    }

    this.setState({
      unsubscriber: Jetemit.on(SocketEvents.CHATS.MESSAGE, (data: any) => {
        if (data.message.chat_id == this.state.chat?.id) {
          if (data.status == Constants.CHAT_RESOLVED_STATUS.RESOLVED) {
            this.load(true);
            this.loadMessages(data.message.chat_id);
            return;
          }

          const currentUser =
            this.props.user?.id === data?.chat?.sender_id
              ? data?.chat?.receiver
              : data?.chat?.sender;
          let messages: any = [...this.state.messages];

          // Si el mensaje confirmado viene del usuario actual, reemplazamos
          // el mensaje local pendiente (sending: true) más antiguo en vez de
          // duplicarlo, igual que en la app móvil.
          if (data.message?.user_id === this.props.user?.id) {
            const pendingIndex = messages.findIndex((m: any) => m.sending);
            if (pendingIndex !== -1) {
              messages[pendingIndex] = data.message;
            } else {
              messages.push(data.message);
            }
          } else {
            messages.push(data.message);
          }

          this.setState(
            {
              messages,
              chat: data.chat,
              currentUser,
            },
            () => {
              this.scrollToBottom();
            },
          );

          if (data.event === SocketEvents.CHATS.ACCEPTED) {
            Globals.showSuccess(
              `El abogado ${currentUser?.name} ${currentUser?.lastname} ha aceptado su chat`,
            );
          }
        }

        if (
          data.chat?.sender_id == this.props.user?.id ||
          data?.chat?.receiver_id == this.props.user?.id
        ) {
          this.setState((prevState: any) => {
            const existingChats = [...prevState.data];
            const chatIndex = existingChats.findIndex((c) => c.id === data.chat.id);

            if (chatIndex !== -1) {
              existingChats.splice(chatIndex, 1);
            }
            existingChats.unshift(data.chat);
            return {
              data: existingChats,
            };
          });

          // Cuando un abogado acepta (o el admin asigna) el chat, se recarga la
          // lista desde el servidor para no depender solo del payload del socket.
          if (data.event === SocketEvents.CHATS.ACCEPTED) {
            this.load(true);
          }
        }
      }),
    });

    this.load();
    this.getSelects();
    this.loadUnreadNotificationsCount();

    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    this.state.unsubscriber();
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const header = document.getElementById('header');
    const containerRouter = document.querySelector(
      '.container-padding-router',
    ) as HTMLElement | null;
    const containerRouter2 = document.querySelector('.container-router') as HTMLElement | null;
    if (!header) return;

    if (window.innerWidth < 768 && (this.state.chat || this.state.viewing_notifications)) {
      header.style.display = 'none';
      if (containerRouter) {
        containerRouter.style.paddingTop = '0px';
      }
      if (containerRouter2) {
        containerRouter2.style.paddingTop = '0px';
      }
    } else {
      header.style.display = '';
      if (containerRouter) {
        containerRouter.style.paddingTop = '40px';
      }
      if (containerRouter2) {
        containerRouter2.style.paddingTop = '50px';
      }
    }
  };

  load = async (withoutLoading = false) => {
    const res: any = await ChatService.get({
      withoutLoading,
      page: this.state.page,
      ...this.state.form,
      user_id: this.props.user?.id,
      per_page: 10,
    });
    this.setState({
      data: res.chats.rows,
      last_page: res.chats.count,
      membership_status: res.access_status,
      plan_user: res.plan_user,
      membership_service: res.membership_service,
    });
  };

  loadUnreadNotificationsCount = async () => {
    const res: any = await NotificationService.client.unreadCount({
      withoutLoading: true,
      withoutError: true,
    });
    this.setState({ unread_notifications_count: res?.count || 0 });
  };

  /**
   * Trae una página de comunicados. El backend siempre devuelve del más
   * nuevo al más viejo (para poder paginar "cargar anteriores" de forma
   * simple); se acumulan tal cual en `notifications` y se invierten recién
   * al renderizar (ver render(), mismo patrón que view-notifications.tsx en
   * mobile) para mostrarlos como un chat: viejo arriba, nuevo abajo.
   */
  loadNotifications = async (page = 1) => {
    this.setState({ notifications_loading: true });
    const res: any = await NotificationService.client.get({
      page,
      per_page: 10,
      withoutLoading: page !== 1,
    });
    const rows = res?.notifications?.rows || [];

    this.setState(
      (prev: any) => ({
        notifications: page === 1 ? rows : [...prev.notifications, ...rows],
        notifications_page: page,
        notifications_pages: res?.notifications?.count || 1,
        notifications_loaded: true,
        notifications_loading: false,
      }),
      () => {
        this.markNotificationsRead(rows);
        if (page === 1) this.scrollNotificationsToBottom();
      },
    );
  };

  loadMoreNotifications = () => {
    if (this.state.notifications_page >= this.state.notifications_pages) return;
    this.loadNotifications(this.state.notifications_page + 1);
  };

  /** Idéntico al de mobile: marca como leídos los que falten y refresca el contador compartido. */
  markNotificationsRead = async (rows: any[]) => {
    const unread = rows.filter((r: any) => !r.read_at);
    if (unread.length === 0) return;

    await Promise.all(
      unread.map((r: any) =>
        NotificationService.client.read({ id: r.id, withoutLoading: true, withoutError: true }),
      ),
    );
    this.loadUnreadNotificationsCount();
  };

  scrollNotificationsToBottom = () => {
    if (this.notificationsEnd) {
      this.notificationsEnd.scrollTo({
        behavior: 'smooth',
        top: this.notificationsEnd.scrollHeight,
      });
    }
  };

  openNotificationImage = (url: string) => {
    window.open(url, '_blank');
  };

  /**
   * "Legalmente" no es un Chat real (no tiene chat.id) -- se abre aparte de
   * loadMessages(), con su propio estado `viewing_notifications`, para no
   * forzar un shape de Chat falso solo para reutilizar el panel derecho.
   */
  openNotifications = () => {
    this.setState({ chat: null, viewing_notifications: true }, () => {
      this.handleResize();
      if (!this.state.notifications_loaded) {
        this.loadNotifications(1);
      } else {
        this.markNotificationsRead(this.state.notifications);
        this.scrollNotificationsToBottom();
      }
    });
  };

  loadMessages = async (chat_id: number) => {
    if (!chat_id) return;
    const res: any = await ChatService.view({
      user_id: this.props.user?.id,
      chat_id,
    });

    const user = this.props.user.id === res.chat?.sender_id ? res.chat?.receiver : res.chat?.sender;

    this.setState(
      (prev: any) => ({
        chat: res.chat,
        viewing_notifications: false,
        currentUser: user,
        data: prev.data.map((i: any) => {
          if (i.id == chat_id) {
            return {
              ...i,
              unread_sender: this.props.user?.id === i.sender_id ? 0 : i.unread_sender,
              unread_receiver: this.props.user?.id === i.receiver_id ? 0 : i.unread_receiver,
            };
          }
          return i;
        }),
        messages: res.messages || [],
      }),
      () => {
        this.scrollToBottom();
        this.handleResize();
      },
    );
  };

  getSelects = async () => {
    const selects: any = await GeneralService.getSelects({
      user_id: this.props.user?.id,
      services: true,
      service_id: Constants.SERVICES.CHAT,
    });

    this.setState({
      price: selects.services[0].price,
    });
  };

  scrollToBottom = () => {
    this.messagesEnd.scrollTo({
      behavior: 'smooth',
      top: this.messagesEnd.scrollHeight,
    });
  };

  autoGrow = (e: any) => {
    if (e.nativeEvent.keyCode == 13) {
      e.preventDefault();
      this.send();
    }
    this.textareaRef.style.height = 'inherit';
    this.textareaRef.style.height = `${Math.max(this.textareaRef.scrollHeight, 55.8)}px`;
  };

  /**
   * Si no hay chat.id todavía, en lugar de llamar ChatService.send
   * (que requiere un chat_id existente) llamamos ChatService.save con el
   * mensaje incluido.
   *  El envío de archivos adjuntos (PaperClip) sigue requiriendo chat_id existente, por lo
   * que ese control se mantiene oculto mientras el chat es pendiente
   */
  send = async (file = '') => {
    if (this.isLawyerDirectoryLocked()) {
      Globals.showError(
        'Para continuar con la conversación, adquiera una membresía que incluya directorio de abogados',
      );
      return;
    }

    if (this.state.form.message || file != '') {
      const message = this.state.form.message;
      this.setState({
        form: {
          ...this.state.form,
          message: '',
        },
        height: 0,
      });

      const isPendingChat = !this.state.chat?.id;

      if (isPendingChat) {
        const pendingLawyer: any = this.state.currentUser;
        const res: any = await ChatService.save({
          sender_id: this.props.user?.id,
          receiver_id: pendingLawyer?.id,
          type: Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES,
          without_membership: true,
          message,
        });

        const newChatId = res?.chat?.id;

        // Actualiza la URL a ?chat_id=X sin recargar la pantalla, para que el
        // chat recién creado quede igual de "recargable"/compartible que el resto.
        this.props.history.replace(`/client/chat?chat_id=${newChatId}`);

        this.load(true);
        if (newChatId) {
          this.loadMessages(newChatId);
        }

        this.setState({ sending_file: false, sending_file_placeholder: false });
        return;
      }

      if (file === '') {
        this.addPendingMessage(message);
      }

      try {
        await ChatService.send({
          chat_id: this.state.chat?.id,
          message,
          user_id: this.props.user?.id,
          withoutLoading: true,
          hasFile: true,
          file,
        });
      } catch (error: any) {
        // Respaldo por si el backend rechaza el envío (límite de mensajes
        // gratuitos del directorio de abogados alcanzado) aunque el frontend
        // todavía no había bloqueado el input localmente. Se recarga el chat
        // para que `chat_lawyer_directory_locked` quede sincronizado y el
        // aviso se muestre en el próximo render.
        Globals.showError(
          error?.response?.data?.message ||
            'Para continuar con la conversación, adquiera una membresía que incluya directorio de abogados',
        );
        if (this.state.chat?.id) {
          this.loadMessages(this.state.chat.id);
        }
      }
      this.setState({ sending_file: false, sending_file_placeholder: false });
    }
  };

  /**
   * Construye y agrega a `messages` un mensaje local optimista con
   * `sending: true`, igual que hace la app móvil, para mostrar el ícono de
   * reloj mientras se confirma el envío por el socket.
   */
  addPendingMessage = (message: string) => {
    const localMessage = {
      id: `local-${Date.now()}`,
      chat_id: this.state.chat?.id,
      user_id: this.props.user?.id,
      message,
      created_at: new Date().toISOString(),
      sending: true,
      user: {
        id: this.props.user?.id,
        name: this.props.user?.name,
        lastname: this.props.user?.lastname,
      },
    };
    this.setState({ messages: [...this.state.messages, localMessage] }, () =>
      this.scrollToBottom(),
    );
  };

  change = (e: any) => {
    this.setState({
      form: {
        ...this.state.form,
        [e.target.name]: e.target.value,
      },
    });
  };

  //Unificamos ACCESS_LIMITED y ACCESS_DENIED en el mismo flujo de pago.
  // Ahora cualquier cliente sin usos disponibles, tenga o no membresía, va al modal de pago.
  create = () => {
    const { membership_status } = this.state;
    if (membership_status === Constants.ACCES_STATUS.ACCESS_GRANTED) {
      // Tiene usos disponibles → abrir modal de nuevo chat directamente
      return this.setState({ visible: true });
    }
    // ACCESS_LIMITED (usos agotados) o ACCESS_DENIED (sin membresía) → flujo de pago por uso
    return this.setState({ visible_consumed: true });
  };

  openModalPayment = () => {
    this.setState({
      visible_payment: true,
      visible_consumed: false,
    });
  };

  onClose = (chat_id: number | null = null, reload: boolean = false) => {
    this.setState(
      {
        visible: false,
        visible_consumed: false,
        visible_payment: false,
      },
      () => {
        if (chat_id || reload) {
          this.load(true);
        }
        if (chat_id) {
          this.loadMessages(chat_id);
        }
      },
    );
  };

  openViewFiles = (e: any, item: any) => {
    e.preventDefault();
    this.setState(
      {
        visible_files: true,
        item,
      },
      () => {
        Globals.hideTooltip();
      },
    );
  };

  onCloseViewFiles = () => {
    this.setState({
      visible_files: false,
      item: null,
    });
  };

  private openFacturaPdf = (relPathIn?: string) => {
    if (!relPathIn) return;

    const base = (Constants.BASE_PUBLIC || '').replace(/\/+$/, '');
    const rel = relPathIn.startsWith('/') ? relPathIn : `/${relPathIn}`;
    const url = `${base}${rel}`;

    if ((window as any).webkit?.messageHandlers?.cordova_iab) {
      const payload = { message: 'open_url', url };
      (window as any).webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(payload));
      return;
    }

    Print(url, true);
  };

  onSuccesPayment = async (data: any) => {
    try {
      const { user } = this.props;
      const res: any = await GeneralService.savePayment({
        user_id: user?.id,
        service_id: Constants.SERVICES.CHAT,
        ...data,
      });

      const relPathObjOrStr = res?.facturaPDF?.pdf?.relPath;
      const relPath =
        typeof relPathObjOrStr === 'string' ? relPathObjOrStr : relPathObjOrStr?.relPath;

      this.openFacturaPdf(relPath);

      this.onClose(this.state.chat?.id ?? null, true);
      Globals.showSuccess('Se ha enviado el pago correctamente, ya puede crear un nuevo chat');
    } catch (error) {
      console.error(error);
      Globals.showError('No se pudo registrar el pago');
    }
  };

  back = () => {
    this.setState(
      {
        chat: null,
        viewing_notifications: false,
      },
      () => this.handleResize(),
    );
  };

  statusColor = (status: number) => {
    switch (status) {
      case Constants.CHAT_RESOLVED_STATUS.PENDING:
        return Colors.orange;
      case Constants.CHAT_RESOLVED_STATUS.RESOLVED:
        return Colors.green;
      case Constants.CHAT_RESOLVED_STATUS.NO_RESOLVED:
        return Colors.danger;
      case Constants.CHAT_RESOLVED_STATUS.REVIEW:
        return Colors.yellow;
      case Constants.CHAT_RESOLVED_STATUS.IN_PROCESS:
        return Colors.cyan2;
    }
  };

  isLawyerDirectoryLocked = (): boolean => {
    // Bloqueo desactivado temporalmente a solicitud del negocio (2026-09-28).
    // Se deja la lógica original comentada para poder reactivarla fácilmente.
    //
    // const chat: Chat | null = this.state.chat;
    // if (!chat || chat.type !== Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES) return false;
    //
    // if ((chat as any)?.chat_lawyer_directory_locked) return true;
    //
    // const isFreeOrNoMembership =
    //   !this.state.plan_user || (this.state.plan_user as any)?.membership?.is_free === true;
    //
    // if (!isFreeOrNoMembership) return false;
    //
    // const userId = this.props.user?.id;
    // const sentByClient = (this.state.messages as any[]).filter(
    //   (m: any) => m.user_id === userId && !m.sending,
    // ).length;
    //
    // return sentByClient >= 3;

    return false;
  };

  openMembership = () => {
    this.setState({
      visible_consumed: false,
      visible_membership: true,
    });
  };

  closeMembership = (reload: boolean = false) => {
    this.setState({
      visible_membership: false,
    });
    if (reload) {
      this.load(true);
    }
  };

  render() {
    const {
      messages,
      visible,
      visible_consumed,
      visible_payment,
      membership_status,
      visible_files,
      currentUser,
      visible_membership,
      viewing_notifications,
      notifications,
      unread_notifications_count,
    } = this.state;
    const isInternal = this.props.user?.internal === Constants.LAWYER_INTERNAL.YES;
    const isClient = this.props.user?.level_id == Constants.LEVELS.CLIENT;
    const chat: Chat | null = this.state.chat;

    return (
      <div id='client-chat'>
        <div className={`container-modal-right ${visible_files ? 'active' : ''}`}>
          {visible_files && (
            <ModalFiles item={this.state.item} onClose={() => this.onCloseViewFiles()} />
          )}
        </div>
        <Modal
          className='modal-create-chat'
          visible={visible}
          title='Nuevo mensaje'
          onClose={() => this.onClose()}
        >
          {/*
		          // @ts-ignore */}
          <ModalCreate onClose={(chat_id: number | null = null) => this.onClose(chat_id)} />
        </Modal>

        {
          <Modal
            className='modal-container-membership'
            visible={visible_membership}
            title=''
            onClose={() => this.closeMembership()}
          >
            {/* @ts-ignore */}
            <AddMembership membership_id={null} onClose={() => this.closeMembership(true)} />
          </Modal>
        }

        {/*Texto dinámico según si el usuario tiene membresía o no */}
        <Modal
          id='modal-consumed-payment'
          visible={visible_consumed}
          title='Consulta un abogado'
          onClose={() => this.onClose()}
        >
          <img src={Alert} />
          <p className='title'>
            {this.state.plan_user !== null
              ? 'Usted ha consumido sus usos de chat'
              : 'No tiene membresía activa'}
          </p>
          <p>
            {this.state.plan_user !== null
              ? 'Puede obtener el servicio mediante pago o esperar el próximo mes'
              : 'Puede pagar por el uso del servicio de chat'}
          </p>
          <div className='btn-container'>
            <button className='btn btn-accept' onClick={this.openModalPayment}>
              Pagar
            </button>
          </div>
        </Modal>

        <Modal
          className='modal-create-chat'
          visible={visible_payment}
          title=''
          onClose={() => this.onClose()}
        >
          {/*// @ts-ignore */}
          <ModalPayment
            onClose={(reload: boolean) => this.onClose()}
            price={this.state.price}
            onSuccess={(data: any) => this.onSuccesPayment(data)}
            title='Consulta un abogado'
          />
        </Modal>

        <div className='row mr-0'>
          <div className='col-md-4 pr-0'>
            <div
              className={`container-list d-md-block ${
                this.state.chat == null && !viewing_notifications ? 'd-block' : 'd-none'
              }`}
            >
              <div className='header-chat'>
                <Input
                  value={this.state.form.search}
                  name='search'
                  placeholder='Buscar'
                  className='input-table'
                  onSubmit={() => this.load()}
                  searchRight={true}
                  onChange={(e: any) => {
                    clearTimeout(this.timer);
                    this.timer = setTimeout(() => {
                      this.load(true);
                    }, 1000);
                    this.change(e);
                  }}
                />
                {/* El botón (+) ahora es visible para cualquier cliente,
                    independientemente de si tiene membresía o usos disponibles.*/}
                {isClient && (
                  <button className='btn btn-add' onClick={() => this.create()}>
                    <img src={AddIcon} />
                    <span>Consulta Interna</span>
                  </button>
                )}
              </div>
              <div className='list'>
                {/* Ítem fijo -- bandeja de comunicados ("Legalmente"), no es un
                    chat real (ver openNotifications()). Siempre primero, incluso
                    sin chats todavía -- mismo patrón que chat.tsx en mobile. */}
                <div
                  className={`item-chat ${viewing_notifications ? 'active' : ''}`}
                  onClick={() => this.openNotifications()}
                >
                  <img src={Logo} />
                  <div style={{ flex: 1 }}>
                    <div className='container-right'>
                      <Render condition={unread_notifications_count > 0}>
                        <div className='unread'>
                          <span>{unread_notifications_count}</span>
                        </div>
                      </Render>
                    </div>
                    <p className='user-chat'>Legalmente</p>
                    <div className='container-flex'>
                      <small className='find-lawyer'>Comunicados</small>
                    </div>
                  </div>
                </div>

                {this.state.data.map((item: any) => {
                  const user =
                    this.props.user?.id === item?.sender_id ? item?.receiver : item?.sender;
                  const unread =
                    this.props.user?.id === item?.sender_id
                      ? item?.unread_sender
                      : item?.unread_receiver;
                  const resolved_status = item?.chatResolved;
                  return (
                    <div
                      className={`item-chat ${item.id == this.state.chat?.id ? 'active' : ''}`}
                      onClick={() => this.loadMessages(item?.id)}
                    >
                      <img src={user?.photo ? Constants.BASE_STORAGE + user?.photo : WhiteImage} />
                      <div style={{ flex: 1 }}>
                        <div className='container-right'>
                          <Render
                            condition={
                              (isClient || isInternal) &&
                              item?.type === Constants.CHAT_TYPE.CLIENT_LAWYER
                            }
                          >
                            <div
                              className='status-container'
                              style={{
                                backgroundColor: this.statusColor(resolved_status?.status),
                              }}
                            >
                              <span>{resolved_status?.status_text}</span>
                            </div>
                          </Render>
                          <Render condition={unread > 0}>
                            <div className='unread'>
                              <span>{unread}</span>
                            </div>
                          </Render>
                        </div>
                        <p className='user-chat'>
                          {user?.name || ''} {user?.lastname || ''}
                        </p>
                        <p className='last-message'>
                          {item.last_message?.file ? 'Archivo Adjunto' : item.last_message?.message}
                        </p>
                        <div className='container-flex'>
                          <Render
                            condition={item?.type === Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES}
                          >
                            <small className='find-lawyer'>Ubica tu abogado</small>
                          </Render>
                          <Render condition={item?.type === Constants.CHAT_TYPE.CLIENT_LAWYER}>
                            <small className='find-lawyer'>Consulta interna</small>
                          </Render>
                          <p className='date'>
                            {moment(item.updated_at || item.created_at).format('DD/MM/YYYY HH:mm')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {this.state.data.length == 0 && (
                  <p className='no-chat'>No se han encontrado chats disponibles</p>
                )}

                <Pagination
                  pages={this.state.last_page}
                  active={this.state.page}
                  onChange={async (page: number) => {
                    await this.setState({
                      page: page,
                    });
                    this.load();
                  }}
                />
              </div>
            </div>
          </div>
          <div className='col-md-8 px-0 chat-messages-container'>
            {viewing_notifications ? (
              <>
                <div className='container-messages-header px-5'>
                  <div className='container-user'>
                    <button className='btn btn-back d-block d-md-none' onClick={() => this.back()}>
                      <img src={BackIcon} />
                    </button>
                    {/* Igual que <User/>, pero sin pasar por Constants.BASE_STORAGE
                        -- Logo es un asset del bundle, no una foto de storage. */}
                    <div id='container-user'>
                      <img src={Logo} className='image' />
                      <div className='container-name'>
                        <p className='name'>Legalmente</p>
                        <p className='description'>Comunicados</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className='container-messages height'
                  ref={(el) => {
                    this.notificationsEnd = el;
                  }}
                >
                  {this.state.notifications_page < this.state.notifications_pages && (
                    <button
                      type='button'
                      className='btn-load-older'
                      disabled={this.state.notifications_loading}
                      onClick={this.loadMoreNotifications}
                    >
                      Ver comunicados anteriores
                    </button>
                  )}

                  {[...notifications].reverse().map((item: any) => (
                    <div className='item-message notification' key={item.id}>
                      <p className='sender'>Legalmente</p>
                      <p className='notification-title'>{item.title}</p>
                      <p className='notification-subject'>{item.subject}</p>
                      <p className='message'>{item.description}</p>

                      {/* Mismo patrón que los archivos adjuntos de un chat real
                          (.link-file): tarjeta ícono+texto, sin miniatura inline. */}
                      {(item.images || []).map((image: any) => (
                        <a
                          href='#'
                          key={image.position}
                          className='notification-file'
                          onClick={(e: any) => {
                            e.preventDefault();
                            this.openNotificationImage(image.url);
                          }}
                        >
                          <img src={ImageIcon} />
                          <p>Imagen adjunta</p>
                        </a>
                      ))}

                      <p className='date'>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</p>
                    </div>
                  ))}

                  {this.state.notifications_loaded && notifications.length === 0 && (
                    <p className='no-chat'>No posee comunicados</p>
                  )}
                </div>
              </>
            ) : chat != null ? (
              <>
                <div className='container-messages-header px-5'>
                  <div className='container-user'>
                    <button className='btn btn-back d-block d-md-none' onClick={() => this.back()}>
                      <img src={BackIcon} />
                    </button>
                    <User
                      image={(currentUser as any)?.photo}
                      /*
                        Cuando el chat viene de "Ubica tu abogado" y
                        todavía es "pendiente" si no hay name/lastname, se usa full_name
                        como respaldo para que el header del chat siempre
                        muestre el nombre del abogado, incluso antes de que
                        exista el chat en BD 
                      */
                      name={
                        (currentUser as any)?.name || (currentUser as any)?.lastname
                          ? `${(currentUser as any)?.name || ''}  ${(currentUser as any)?.lastname || ''}`
                          : (currentUser as any)?.full_name || ''
                      }
                      description={
                        this.state.chat?.type == Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES
                          ? 'Ubica tu abogado'
                          : ''
                      }
                    />
                  </div>

                  <div className='container-flex'>
                    {/*
                      
                      Ocultamos este ícono mientras el chat es "pendiente"
                      (chat.id == null, es decir, aún no se envió el primer mensaje),
                      para evitar que el usuario intente ver archivos de un chat que
                      todavía no existe.
                    */}
                    <Render
                      condition={
                        this.state.chat?.type == Constants.CHAT_TYPE.CLIENT_LAWYER_WITH_FILES &&
                        !!this.state.chat?.id
                      }
                    >
                      <div className='view-files'>
                        <Tooltip title='Ver archivos'>
                          <a
                            className='link-icon'
                            href='#'
                            onClick={(e: any) => this.openViewFiles(e, this.state.chat)}
                          >
                            <img src={PaperclipIcon} />
                          </a>
                        </Tooltip>
                      </div>
                    </Render>
                    <Render
                      condition={
                        (isClient || isInternal) && chat?.type === Constants.CHAT_TYPE.CLIENT_LAWYER
                      }
                    >
                      <div
                        className='status-container'
                        style={{
                          backgroundColor: this.statusColor((chat as any)?.chatResolved?.status),
                        }}
                      >
                        <span>{(chat as any)?.chatResolved?.status_text}</span>
                      </div>
                    </Render>
                  </div>
                </div>

                <div
                  className='container-messages height'
                  ref={(el) => {
                    this.messagesEnd = el;
                  }}
                >
                  {messages.map((item: any) => {
                    if (!item.file && !item.message) return;

                    if (item.file) {
                      const ext = item.file.split('.').pop()?.toLowerCase();
                      const IMAGE_EXTS = [
                        'png',
                        'jpg',
                        'jpeg',
                        'gif',
                        'webp',
                        'bmp',
                        'tiff',
                        'tif',
                        'avif',
                        'heic',
                        'heif',
                      ];
                      const fileLabel = IMAGE_EXTS.includes(ext) ? 'Imagen' : ext?.toUpperCase();
                      return (
                        <a
                          href='#'
                          onClick={(e: any) => {
                            e.preventDefault();
                            StorageService.openFile(item.file);
                          }}
                          className={`link-file ${item.user_id == this.props.user?.id ? 'self' : ''}`}
                        >
                          <img src={IMAGE_EXTS.includes(ext) ? ImageIcon : Document2Icon} />
                          <p>
                            Archivo Adjunto <span>({fileLabel})</span>
                          </p>
                          <p className='date'>
                            {moment(item.created_at).format('DD/MM/YYYY HH:mm')}
                          </p>
                        </a>
                      );
                    } else {
                      return (
                        <div
                          className={`item-message ${item.user_id == this.props.user?.id ? 'self' : ''}`}
                        >
                          <p className='message'>{item.message}</p>
                          {item.sending ? (
                            <img src={ClockIcon} className='clock-icon' alt='Enviando' />
                          ) : (
                            <p className='date'>
                              {moment(item.created_at).format('DD/MM/YYYY HH:mm')}
                            </p>
                          )}
                        </div>
                      );
                    }
                  })}
                  {this.state.sending_file_placeholder && (
                    <FileUploadPlaceholder isImage={this.state.uploading_is_image} />
                  )}
                </div>
                <div style={{ padding: '20px' }}>
                  {this.isLawyerDirectoryLocked() ? (
                    <div className='container-textarea container-lawyer-directory-locked'>
                      <p className='lawyer-directory-locked-message'>
                        Para continuar con la conversación, adquiera una membresía que incluya
                        directorio de abogados
                      </p>
                      <button className='btn btn-accept' onClick={this.openMembership}>
                        Adquirir membresía
                      </button>
                    </div>
                  ) : (
                    <div className='container-textarea'>
                      <div className='container-white'>
                        <textarea
                          className='form-control'
                          ref={(ref) => (this.textareaRef = ref)}
                          rows={1}
                          value={this.state.form.message}
                          placeholder='Escribir'
                          disabled={chat.status === Constants.CHAT_STATUS.INACTIVE}
                          name='message'
                          style={{ minHeight: 38, resize: 'none' }}
                          onKeyDown={(e: any) => this.autoGrow(e)}
                          onChange={this.change}
                        />
                        <Render
                          condition={chat.status === Constants.CHAT_STATUS.ACTIVE && !!chat.id}
                        >
                          {/*
                            Subir un documento adjunto (PaperClip) llama a
                            send(file) que internamente usa ChatService.send con
                            chat_id, endpoint que requiere un chat ya existente.
                            Se oculta mientras chat.id es null (chat
                            pendiente, sin primer mensaje aún) para no intentar subir
                            un archivo a un chat que no existe en BD.
                          */}
                          <PaperClip
                            formats={[
                              'pdf',
                              'doc',
                              'docx',
                              'xls',
                              'xlsx',
                              'ppt',
                              'pptx',
                              'png',
                              'jpg',
                              'jpeg',
                              'heic',
                              'heif',
                              'webp',
                              'bmp',
                              'tiff',
                              'tif',
                              'avif',
                            ]}
                            successText='Subir documento'
                            pendingText='Subir documento'
                            value={''}
                            onChange={(e: any) => {
                              this.setState(
                                {
                                  sending_file: true,
                                  sending_file_placeholder: true,
                                  uploading_is_image: e.target.value?.type === 'image/png',
                                },
                                () => this.scrollToBottom(),
                              );
                              this.send(e.target.value);
                            }}
                          />
                        </Render>
                      </div>
                      <div>
                        <Render condition={chat.status === Constants.CHAT_STATUS.ACTIVE}>
                          <button
                            className={`btn${this.state.sending_file ? ' btn-sending' : ''}`}
                            onClick={() => this.send()}
                            disabled={this.state.sending_file}
                          >
                            <img src={SendIcon} className='send-icon' />
                          </button>
                        </Render>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className='no-items d-none d-md-block'>
                Seleccione un chat a la izquierda{' '}
                {this.props.user?.level_id == Constants.LEVELS.CLIENT ? 'o cree uno nuevo' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
    pending_lawyer: state.pending_lawyer,
  };
})(ChatScreen);
